import React from 'react';
import PropTypes from 'prop-types';
import { CheckCircle, XCircle } from 'lucide-react';

const PasswordValidationFeedback = ({ password, className = '' }) => {
  const validationRules = [
    {
      id: 'length',
      test: password => password.length >= 8,
      label: 'At least 8 characters long'
    },
    {
      id: 'uppercase',
      test: password => /[A-Z]/.test(password),
      label: 'One uppercase letter (A-Z)'
    },
    {
      id: 'lowercase',
      test: password => /[a-z]/.test(password),
      label: 'One lowercase letter (a-z)'
    },
    {
      id: 'number',
      test: password => /\d/.test(password),
      label: 'One number (0-9)'
    },
    {
      id: 'special',
      test: password => /[@$!%*?&#^()\-_+=\[\]{}|:;"'<>,.~`]/.test(password),
      label: 'One special character (@$!%*?&#^()-_+=[]{}|:;"\u0027<>,.~`)'
    }
  ];

  const getPasswordStrength = () => {
    const passedRules = validationRules.filter(rule => rule.test(password)).length;
    
    if (passedRules <= 2) return { label: 'Weak', color: 'text-red-600', bgColor: 'bg-red-500' };
    if (passedRules <= 4) return { label: 'Good', color: 'text-yellow-600', bgColor: 'bg-yellow-500' };
    return { label: 'Strong', color: 'text-green-600', bgColor: 'bg-green-500' };
  };

  const strength = getPasswordStrength();
  const passedRules = validationRules.filter(rule => rule.test(password)).length;

  if (!password) return null;

  return (
    <div className={`mt-3 p-3 bg-gray-50 rounded-lg ${className}`}>
      {/* Password Strength Indicator */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Password strength:</span>
          <span className={`text-sm font-medium ${strength.color}`}>
            {strength.label}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`h-2 rounded-full transition-all duration-300 ${strength.bgColor}`}
            style={{ width: `${(passedRules / 5) * 100}%` }}
          />
        </div>
      </div>

      {/* Validation Rules */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">Requirements:</p>
        <ul className="space-y-1">
          {validationRules.map(rule => {
            const isValid = rule.test(password);
            return (
              <li key={rule.id} className="flex items-center gap-2 text-sm">
                {isValid ? (
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                )}
                <span className={isValid ? 'text-green-700' : 'text-gray-600'}>
                  {rule.label}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

PasswordValidationFeedback.propTypes = {
  password: PropTypes.string.isRequired,
  className: PropTypes.string
};

export default PasswordValidationFeedback;